import { memo, useCallback } from "react";
import ModalWrapper from "..";
import { useTranslation } from "react-i18next";
import { Button } from "../../button";
import toast from "react-hot-toast";
import {
  EVACUATION_EVENT_PARTICIPANTS_STATUSES,
  EVACUATION_METHODS,
} from "../../../constants/common";
import { useUpdateEvacuationEventParticipantStatus } from "../../../hooks/emergency-evacuation/mutation";
import * as Yup from "yup";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../../inputs/TextInput";

interface ExitedEvacuationEventParticipantModalProps {
  selectedEventId: number | null;
  isOpen: boolean;
  onOpenChange: () => void;
}

const ExitedEvacuationEventParticipantModalSchema = Yup.object({
  remarks: Yup.string(),
});

interface FormData {
  remarks?: string;
}

const ExitedEvacuationEventParticipantModal = ({
  selectedEventId,
  isOpen,
  onOpenChange,
}: ExitedEvacuationEventParticipantModalProps) => {
  const { t } = useTranslation();

  const { handleSubmit, control } = useFormWithSchema(
    ExitedEvacuationEventParticipantModalSchema,
  );

  const {
    mutateAsync: UpdateEvacuationEventParticipantStatus,
    isPending: isUpdateEvacuationEventParticipantStatusLoading,
  } = useUpdateEvacuationEventParticipantStatus();

  const onSafe = useCallback(
    async (data: FormData) => {
      if (!selectedEventId) return;

      try {
        const body = {
          n_EvacuationEventID: selectedEventId,
          n_EvacuationMethod: EVACUATION_METHODS.MANUAL,
          n_EvacuationStatus: EVACUATION_EVENT_PARTICIPANTS_STATUSES.EXITED,
          S_Remarks: data.remarks,
        };

        await UpdateEvacuationEventParticipantStatus(body);

        toast.success(t("participantMarkedExited"));
        onOpenChange();
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [UpdateEvacuationEventParticipantStatus, onOpenChange, selectedEventId, t],
  );

  return (
    <ModalWrapper isOpen={isOpen} onOpenChange={onOpenChange}>
      <form
        onSubmit={handleSubmit(onSafe)}
        className=" flex flex-col items-center justify-center gap-5 w-full"
      >
        <p className="text-lg font-medium">{t("confirmParticipantExited")}</p>

        <div className="w-[90%] sm:w-3/4">
          <Controller
            name="remarks"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("remarks")}
                onChange={onChange}
                value={value}
              />
            )}
          />
        </div>

        <div className="flex gap-2 items-center">
          <Button isLoading={isUpdateEvacuationEventParticipantStatusLoading}>
            {t("yesMarkExit")}
          </Button>
          <Button
            onClick={onOpenChange}
            className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
          >
            {t("close")}
          </Button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default memo(ExitedEvacuationEventParticipantModal);
